import type { AmbientMedication, SoapPayload } from './ambient-types';

/**
 * THE DISPOSITION CONTRACT — every medication mention renders exactly once.
 *
 * ── THE DEFECT CLASS THIS REMOVES ─────────────────────────────────────────────────────────────
 *
 * `medications[]` used to become order proposals one-for-one. Everything the consultation said
 * about a drug therefore arrived on screen as a prescription with a tick beside it:
 *
 *     "She's not on metformin"              -> a metformin order
 *     "If the pain returns, take ibuprofen"  -> an ibuprofen order
 *     "We could consider a statin"           -> a statin order
 *
 * And the mirror of it, which is worse because nothing is on screen to argue with: when Master's
 * own non-prescriber filter stripped an order, the MENTION went with it. A medication the patient
 * reported, or a nurse repeated, simply never appeared. **A mention that vanishes because its
 * action was refused is a silent clinical loss** — the exact failure this workstream exists to
 * remove, and the reason the rule below is stated as two separate decisions rather than one.
 *
 * ── THE CONTRACT, IN FULL ─────────────────────────────────────────────────────────────────────
 *
 * Every entry in `medications[]` renders EXACTLY ONCE, as EXACTLY ONE of:
 *
 *     an ORDER PROPOSAL   a card with an accept control
 *     Continuing          the patient is already on it and it carries on
 *     Patient reports     the patient said it; it is history, not a prescription
 *     Advised against     the consultation said not to
 *     Mentioned           everything else — said, not acted on
 *
 * TWO DECISIONS, NOT ONE. `dispositionOf` answers "what kind of mention is this?"; `actionRefusal`
 * answers "may an action follow?". **Suppressing an action never suppresses the mention.** A
 * refusal downgrades an order-intent mention to `'mentioned'` and keeps it on screen carrying the
 * reason; it never removes a row.
 *
 * NO ACTION UNLESS ALL THREE HOLD:
 *
 *   1. the assertion is AFFIRMATIVE      — not negated, not advised against
 *   2. it is ABOUT THE PATIENT           — not a family member's, not someone else's
 *   3. the SPEAKER IS A PRESCRIBER       — not the patient, not a non-prescribing role
 *
 * and, separately, a CONDITIONAL or HYPOTHETICAL mention never produces one: "if the pain returns"
 * and "we could consider" describe a drug that was discussed, not one that was given.
 *
 * ── SILENCE IS NEVER A REFUSAL ────────────────────────────────────────────────────────────────
 *
 * Every predicate below refuses only on a POSITIVE contrary signal. An absent or unrecognised
 * `disposition` / `speaker` behaves exactly as `encounter-from-transcript/1` did, so a server that
 * has not shipped the new fields loses nothing — and, more importantly, a word nobody here
 * anticipated cannot silently delete a prescription from the panel.
 *
 * This is the same rule `reconcile-proposals` runs on ("withholds only what TA4H positively
 * contradicts — never on silence"), and it is deliberate on both. A guard that fires on the
 * unknown is a guard that gets quieter every time the wire gains a word.
 *
 * ── DETERMINISTIC ─────────────────────────────────────────────────────────────────────────────
 *
 * Pure, total and order-fixed: same payload in, same dispositions out, so a re-analysis cannot
 * move a drug from one group to another and a clinician who learns where things go stays right.
 * `medication-disposition.test.ts` mutates each safety rule off one at a time and confirms the
 * suite goes red — a rule nobody can break on purpose is a rule nobody is testing.
 */

/** What a medication mention IS. Exactly one per entry in `medications[]`. */
export type MedicationDisposition =
  | 'order'
  | 'continuing'
  | 'patient-reported'
  | 'advised-against'
  | 'mentioned';

/**
 * Why no action followed. Absent means one did, or that the mention never proposed one.
 *
 * Carried onto the row rather than dropped, because the clinician has to be able to tell "nobody
 * suggested this drug" from "this drug was suggested and I am not being offered it".
 */
export type ActionRefusal =
  | 'not-asserted'
  | 'not-actual'
  | 'not-this-patient'
  | 'not-a-prescriber';

/**
 * A mention's disposition — the five minus `'order'`.
 *
 * `'order'` is EXCLUDED at the type level rather than by convention, because a mention row and an
 * order card are two renderings of one payload entry and the whole contract is that exactly one of
 * them exists. A `MedicationMention` carrying `'order'` would be the duplicate, and the exclusion is
 * what makes the DS `AMBIENT_MENTION_LABELS` total over this type instead of needing a fallback for a case that
 * must never occur.
 */
export type MentionDisposition = Exclude<MedicationDisposition, 'order'>;

/**
 * One medication mention, resolved.
 *
 * `id` is the POSITIONAL id `to-proposals` would have minted for the same entry (`medication-3`),
 * deliberately: the mention and the order proposal are two renderings of one payload row, and
 * sharing the key is what lets `medication-renders-exactly-once.test.tsx` prove that exactly one
 * of them exists for every index.
 */
export interface MedicationMention {
  id: string;
  /** The drug, as the payload names it. */
  display: string;
  /** The sig AS SAID (`sigVerbatim`), never the model's rewriting of it. Absent is normal. */
  sig?: string;
  disposition: MentionDisposition;
  refusal?: ActionRefusal;
}

const norm = (value: string | undefined): string =>
  (value ?? '').trim().toLowerCase().replace(/[\s_]+/g, '-');

/**
 * The disposition vocabulary, grouped by what it MEANS rather than by who says it.
 *
 * Every word is a guess about Master's spelling and is treated as one: a disposition in none of
 * these sets falls through to `'mentioned'`, which is visible, inert and safe. Adding a word here
 * is a one-line change; omitting one costs a clinician a second look, never a prescription.
 */
const ORDER_INTENT: ReadonlySet<string> = new Set([
  // MASTER'S OWN WORD, first in the list because it is the only one it actually sends.
  // `tools/ambient-eval/prompts/*.txt` emits `disposition: "new-order"`; membership here is exact,
  // and without this entry EVERY dictated prescription fell through to `'mentioned'` and the
  // ambient panel produced no order card at all. `disposition-wire-vocabulary.test.ts` pins it.
  'new-order',
  'order', 'ordered', 'new', 'start', 'started', 'starting', 'initiate', 'initiated',
  'prescribe', 'prescribed', 'prescription', 'change', 'changed', 'adjust', 'adjusted',
]);
const CONTINUING: ReadonlySet<string> = new Set([
  'continue', 'continued', 'continuing', 'ongoing', 'unchanged', 'maintain', 'maintained',
  'refill', 'renew', 'renewed',
]);
const PATIENT_REPORTED: ReadonlySet<string> = new Set([
  'reported', 'patient-reported', 'self-reported', 'home', 'home-medication', 'historical',
  'history', 'taking',
]);
/**
 * Advised against — INCLUDING every form of stopping.
 *
 * A stop is not a quieter order, it is the opposite instruction, and grouping it with
 * `'mentioned'` would hide the one disposition a clinician most needs to see beside a drug the
 * patient may still have in the cupboard.
 */
const ADVISED_AGAINST: ReadonlySet<string> = new Set([
  'advised-against', 'advise-against', 'avoid', 'contraindicated', 'declined', 'refused',
  'stop', 'stopped', 'discontinue', 'discontinued', 'hold', 'held', 'withheld',
  'not-taking', 'negated', 'denied',
  // `ruled-out` is Master's OWN negative certainty on `diagnoses[].certainty` (the other value is
  // `confirmed`). It reaches this set through `assertedNegative`, which `decideAccept`'s fifth
  // direct-add condition reads -- and which matched nothing Master sends until this line existed,
  // so "we've excluded a PE" was written straight into the A&P. `excluded` rides along as the
  // obvious synonym. NOT `rule-out`: clinical shorthand for a diagnosis still to be excluded, i.e.
  // suspected, which is the opposite claim and must not be read as a negation.
  'ruled-out', 'excluded',
]);
/** Said and not acted on. Named rather than left to the fallthrough, so the intent is readable. */
const DISCUSSED: ReadonlySet<string> = new Set([
  'mentioned', 'discussed', 'considered', 'consider', 'deferred', 'none',
]);

/**
 * Conditional or hypothetical — "if the pain returns take ibuprofen", "we could consider a statin".
 *
 * A SEPARATE AXIS from the disposition words above, because a payload can carry both: a
 * conditional ORDER is still conditional, and reading only the disposition would let "if it comes
 * back, start amoxicillin" through as a prescription written today.
 */
const NOT_ACTUAL: ReadonlySet<string> = new Set([
  'conditional', 'hypothetical', 'prn-conditional', 'if-needed-conditional', 'proposed',
  'possible', 'suggested', 'planned-conditional',
  // PROD-2603. `suspected` is Master's OWN word on `diagnoses[].certainty` for a workup in progress
  // (`tools/ambient-eval/prompts/physician-longevity.txt:54`: 'Workup in progress is certainty
  // "suspected"'). It was in none of these sets, so `decideAccept` read it as asserted and a
  // diagnosis still being worked up was written straight into the A&P -- PROD-1951 again.
  // `disposition-wire-vocabulary.test.ts` pins it against the prompt.
  //
  // The synonyms ride along because each says the same clinical thing: not yet established.
  // `rule-out` / `r/o` are shorthand for a diagnosis STILL TO BE excluded -- i.e. suspected -- and
  // are the opposite claim from `ruled-out` (in `ADVISED_AGAINST`), which says it already was.
  'suspected', 'suspect', 'suspicion', 'working', 'working-diagnosis', 'provisional',
  'differential', 'differential-diagnosis', 'query', 'queried', 'rule-out', 'r/o', 'to-rule-out',
  'probable', 'presumed', 'presumptive', 'tentative', 'unconfirmed',
]);

/** Someone else's medication. "His father takes warfarin" is not this patient's list. */
const NOT_THIS_PATIENT: ReadonlySet<string> = new Set([
  'other', 'family', 'family-member', 'relative', 'someone-else', 'third-party',
]);

/**
 * The same rule read off `speaker`, MINUS the bare word `other`.
 *
 * On `disposition` / `category`, `other` means "someone else's drug". On `speaker` it does not:
 * Master's rule 8 says "anyone else (nurse, family, guardian) is `other`", so `speaker: 'other'` is
 * a person in the room who may not prescribe, speaking about THIS patient. Both readings withhold
 * the order, so nothing about safety turns on this; what turns on it is the sentence the clinician
 * is shown. "Not this patient's medication" beside a drug the nurse repeated back is simply untrue,
 * and a refusal note that is untrue is how a clinician learns to stop reading them.
 *
 * `family` / `relative` stay in both sets and keep resolving as `not-this-patient` — a relative
 * speaking is nearly always a relative speaking about themselves.
 */
const NOT_THIS_PATIENT_SPEAKERS: ReadonlySet<string> = new Set(
  [...NOT_THIS_PATIENT].filter((s) => s !== 'other'),
);

/**
 * Speakers who may prescribe, and speakers who may not.
 *
 * Both sets are explicit and an unrecognised speaker is in NEITHER — it is not a refusal. The
 * whole point of the non-prescriber rule is to stop a patient's own sentence becoming a
 * prescription; it is not a licence to delete a mention whose speaker label this file has never
 * seen.
 *
 * `nurse` sits on the non-prescribing side, matching `accept-verb.ts`'s `PRESCRIBING` set, which
 * already withholds medication verbs from a nurse for the same reason.
 */
const NON_PRESCRIBING_SPEAKERS: ReadonlySet<string> = new Set([
  'patient', 'caregiver', 'carer', 'family', 'relative', 'guardian', 'interpreter',
  'nurse', 'technician', 'coach', 'nutritionist', 'assistant', 'other',
]);

/**
 * PROD-2539 — THE ONLY SPEAKER LABELS THAT MAY CARRY AN ORDER PROPOSAL. Eligibility, never authority.
 *
 * Live diarization has TWO roles. A third person in the room — a guardian, a nurse, an interpreter —
 * is labelled as one of those two before the prompt ever sees the transcript, so a guardian's "she
 * should go back on the antibiotic" can arrive here as `speaker: 'provider'`. No label can therefore
 * AUTHORIZE anything, and nothing in this file treats one as authorization: a label on this list
 * only lets a proposal CARD exist, and a card writes nothing. The order exists only when the
 * authenticated clinician looking at the screen presses its control and completes the order dialog
 * (`decideAccept` routes every medication to `seed-order-search`, never to a direct write), and the
 * card shows whom the transcript attributed it to (`speakerAttributionLine`) so she can see the claim
 * she is confirming.
 *
 * What the list DOES decide is the safe default. A speaker label that is NOT on it — `other`, a raw
 * `Guest-3`, `unknown`, or an EMPTY label on a v2 payload (Master's rule 7 leaves a field "" when the
 * model is unsure) — produces no card at all: the mention renders, carrying `not-a-prescriber`, and
 * she orders it herself if it was hers. "The model could not say who spoke" resolves to "no order
 * proposed", never to "assume it was the doctor".
 *
 * The one exception is a payload carrying NONE of the v2 fields — no disposition, no category, no
 * speaker — which is `encounter-from-transcript/1`, whose every medication was a proposal. That
 * shape is kept exactly, as the header's "silence is never a refusal" promises; a v2 payload that
 * has a disposition and no speaker is not silence, it is a model declining to attribute.
 */
const PRESCRIBING_SPEAKERS: ReadonlySet<string> = new Set([
  'provider', 'practitioner', 'doctor', 'physician', 'clinician', 'prescriber',
]);

/** Whether this mention's speaker label may carry an order PROPOSAL. See `PRESCRIBING_SPEAKERS`. */
function speakerMayPropose(med: AmbientMedication): boolean {
  const speaker = norm(med.speaker);
  if (PRESCRIBING_SPEAKERS.has(speaker)) return true;
  const v1 = !speaker && !norm(med.disposition) && !norm(med.category);
  return v1;
}

/**
 * The line an order CARD carries saying whom the transcript attributed the order to (PROD-2539).
 *
 * Shown because the attribution is a CLAIM, made by a two-role diarizer and a model, and the
 * clinician is the one confirming it. Absent for a v1 payload, which attributes nothing.
 */
export function speakerAttributionLine(med: AmbientMedication): string {
  const speaker = med.speaker?.trim();
  if (!speaker) return '';
  const label = speaker.charAt(0).toUpperCase() + speaker.slice(1).toLowerCase();
  return `Attributed to "${label}" by the transcript's speaker labels, which are not verified. Only your order makes it one.`;
}

/** The patient's own voice. Distinguished from every other non-prescriber because it has a LABEL. */
const PATIENT_SPEAKERS: ReadonlySet<string> = new Set(['patient', 'caregiver', 'carer']);

/**
 * Positively negated — the consultation said NOT this drug.
 *
 * Exported because `decideAccept`'s diagnosis branch reads the same signal off
 * `AmbientDiagnosis.certainty`, and two modules reading one wire field with different rules is
 * precisely how `to-objective-findings` and `soap-class` once disagreed about `negativePossible`.
 */
export function assertedNegative(value: string | undefined): boolean {
  const v = norm(value);
  if (!v) return false;
  return ADVISED_AGAINST.has(v) || v.startsWith('negative') || v.startsWith('not-');
}

/**
 * Positively uncertain — hedged, conditional or hypothetical.
 *
 * `endsWith('possible')` matches TA4H's own `neutralPossible` / `positivePossible` /
 * `negativePossible`, so a certainty word that arrives in the analyser's vocabulary rather than
 * Master's is still caught.
 */
export function assertedUncertain(value: string | undefined): boolean {
  const v = norm(value);
  if (!v) return false;
  return NOT_ACTUAL.has(v) || v.endsWith('possible') || v.startsWith('uncertain');
}

/**
 * What a diagnosis's `certainty` CLAIMS, for the one decision that can write it into the A&P.
 *
 *   'confirmed'  the consultation positively asserted it       -> may be added directly
 *   'suspected'  workup in progress / hedged / to be excluded   -> assessment text, "Suspected:"
 *                (the word `probable` itself reads "Probable:", see `isProbable`)
 *   'ruled-out'  positively excluded                            -> assessment text, "Ruled out:"
 *   'unknown'    EMPTY or a word this file has never seen       -> assessment text,
 *                                                                  "Certainty not stated:"
 *
 * THE INVERSE OF THE MEDICATION RULE, AND DELIBERATELY SO. For a medication mention, silence is
 * never a refusal (see this file's header) because refusing on the unknown would delete a row. For
 * a diagnosis the question is not "does it render" -- it always renders -- but "may it be written
 * without the clinician choosing it", and there the unknown must fail CLOSED. Master's proto says so
 * in as many words (`MasterManagement.proto:331`: "empty certainty is NOT a confirmed one"), and
 * its own prompt rule 7 tells the model to leave a field "" when unsure. So only a positive
 * `confirmed` (or an obvious synonym) earns the direct write; everything else goes to the A&P
 * assessment TEXT (`append-assessment`, rule 5), never the diagnosis block — and not the pre-filled
 * code picker either, which was two clicks from a Final DG1 on the HIE.
 *
 * Negative is checked first so a hedged negation (`negativePossible`) reads as the stronger claim.
 */
export type DiagnosisCertainty = 'confirmed' | 'suspected' | 'ruled-out' | 'unknown';

const CONFIRMED: ReadonlySet<string> = new Set([
  'confirmed', 'definite', 'definitive', 'established', 'diagnosed', 'positive',
]);

export function diagnosisCertainty(value: string | undefined): DiagnosisCertainty {
  if (assertedNegative(value)) return 'ruled-out';
  if (assertedUncertain(value)) return 'suspected';
  if (CONFIRMED.has(norm(value))) return 'confirmed';
  return 'unknown';
}

/**
 * Is the certainty the word `probable` itself? For the assessment LINE's label only (rule 5).
 *
 * Deliberately NOT a fifth `DiagnosisCertainty`: `probable` is still `'suspected'` for every
 * decision (it is not confirmed, so it is never written into the diagnosis block), and a reader
 * that switches on the four values must not learn a new one. Only the words differ: a clinician who
 * said "probable" reads "Probable:" rather than a weaker-sounding "Suspected:".
 */
export function isProbable(value: string | undefined): boolean {
  return norm(value) === 'probable';
}

/**
 * Is the certainty an EXPLICIT hedge — a word that says "possibly present, not established"? For the
 * assessment LINE's label only (rule 5): "Suspected:" / "Probable:" are written for these and no
 * others.
 *
 * NARROWER THAN `'suspected'` ON PURPOSE, for the reason `explicitlyExcluded` is narrower than
 * `'ruled-out'`. `assertedUncertain` accepts any word ending in `possible` (to catch TA4H's own
 * vocabulary), so "impossible", "no longer possible" and "notPossible" classify as suspected. That
 * is safe for the DECISION — none is confirmed, so none reaches the diagnosis block — and false in
 * the WORDS: "Suspected: Pulmonary embolism" for a model that said "impossible". Those read
 * "Certainty not stated".
 */
export function explicitlyHedged(value: string | undefined): boolean {
  const v = norm(value);
  return NOT_ACTUAL.has(v) || v === 'neutralpossible' || v === 'positivepossible' || v === 'uncertain';
}

/** The words that EXPLICITLY say a diagnosis was excluded: Master's own, and its two synonyms. */
const EXCLUDED: ReadonlySet<string> = new Set(['ruled-out', 'excluded', 'negative']);

/**
 * Does the certainty EXPLICITLY say the diagnosis was excluded? For the assessment LINE's label
 * only (rule 5): "Ruled out:" is written for these words and no others.
 *
 * NARROWER THAN `'ruled-out'` ON PURPOSE. `diagnosisCertainty` reads a certainty as ruled-out
 * through `assertedNegative`, which is built for medication stop words: any `not-…` or
 * `negative…` prefix, `hold`, `denied`. That is right for the DECISION — none of them may be
 * written into the diagnosis block — and wrong for the WORDS: "not ruled out" (still possible),
 * "not confirmed" and `negativePossible` would each read "Ruled out", a false exclusion in the
 * note. Those read "Certainty not stated", which claims neither an exclusion nor a diagnosis.
 */
export function explicitlyExcluded(value: string | undefined): boolean {
  return EXCLUDED.has(norm(value));
}

/** The three-part gate, and the conditionality check that rides beside it. */
function actionRefusal(med: AmbientMedication): ActionRefusal | undefined {
  const disposition = norm(med.disposition);
  const category = norm(med.category);
  const speaker = norm(med.speaker);

  // 1. AFFIRMATIVE. A negated or advised-against mention can never carry an action, whoever said
  //    it and whatever else the payload claims.
  if (assertedNegative(med.disposition) || assertedNegative(med.category)) return 'not-asserted';

  // 2. ACTUAL. "If the pain returns" and "we could consider" describe a drug that was discussed.
  //    Checked against both fields because either can carry the conditionality.
  if (assertedUncertain(med.disposition) || assertedUncertain(med.category)) return 'not-actual';

  // 3. ABOUT THE PATIENT. A family member's medication is not this patient's list, and an order
  //    written from one is a prescription for a person who is not in the room.
  if (NOT_THIS_PATIENT.has(disposition) || NOT_THIS_PATIENT.has(category) ||
      NOT_THIS_PATIENT_SPEAKERS.has(speaker)) {
    return 'not-this-patient';
  }

  // 4. PRESCRIBER. Only on a POSITIVE non-prescribing speaker — an absent or unrecognised one is
  //    silence, and silence is the pre-contract behaviour rather than a refusal.
  if (speaker && NON_PRESCRIBING_SPEAKERS.has(speaker)) return 'not-a-prescriber';

  return undefined;
}

/**
 * Which of the five this mention is. Pure, total, deterministic.
 *
 * ORDER IS THE CONTRACT, and each step is a rule a mutation test turns off:
 *
 *   1. negated/advised-against beats everything. A stop instruction misfiled as a mention is the
 *      one label whose loss can leave a patient taking something they were told to stop.
 *   2. the patient's own voice is labelled as such, so history never reads as a prescription.
 *   3. an explicit continuation is `'continuing'` — it is a real clinical fact and not an order.
 *   4. an order intent produces `'order'` ONLY if the gate lets it. Otherwise it degrades to
 *      `'mentioned'` and the refusal travels with it. THE ROW NEVER DISAPPEARS.
 *   5. anything else is `'mentioned'`, which is the fallthrough for every word this file has never
 *      seen.
 */
export function dispositionOf(med: AmbientMedication): {
  disposition: MedicationDisposition;
  refusal?: ActionRefusal;
} {
  const refusal = actionRefusal(med);
  const disposition = norm(med.disposition);
  const category = norm(med.category);

  if (refusal === 'not-asserted') return { disposition: 'advised-against', refusal };

  if (PATIENT_SPEAKERS.has(norm(med.speaker))) {
    return { disposition: 'patient-reported', ...(refusal ? { refusal } : {}) };
  }

  if (PATIENT_REPORTED.has(disposition) || PATIENT_REPORTED.has(category)) {
    return { disposition: 'patient-reported', ...(refusal ? { refusal } : {}) };
  }

  if (CONTINUING.has(disposition) || CONTINUING.has(category)) {
    return { disposition: 'continuing', ...(refusal ? { refusal } : {}) };
  }

  // Said and explicitly not acted on. Named rather than left to the fallthrough below, so the
  // difference between "Master told us this was only discussed" and "this file has never seen
  // that word" stays legible in the code even though both render identically.
  if (DISCUSSED.has(disposition) || DISCUSSED.has(category)) {
    return { disposition: 'mentioned', ...(refusal ? { refusal } : {}) };
  }

  /**
   * ORDER INTENT. Absent on both fields means `encounter-from-transcript/1`, whose every
   * medication was an order proposal — so absence keeps that behaviour exactly, and a v1 server
   * loses no prescription to this change. A word in `DISCUSSED`, or any word this file does not
   * know, is NOT order intent.
   */
  const orderIntent =
    ORDER_INTENT.has(disposition) || ORDER_INTENT.has(category) || (!disposition && !category);

  if (!orderIntent) return { disposition: 'mentioned', ...(refusal ? { refusal } : {}) };
  if (refusal) return { disposition: 'mentioned', refusal };
  // PROD-2539. An order intent from a speaker label that is not a confirmed prescriber label is a
  // mention, not a card. Checked HERE, at the order step only, so a continuing or patient-reported
  // row with no speaker is not handed a refusal note it did nothing to earn.
  if (!speakerMayPropose(med)) return { disposition: 'mentioned', refusal: 'not-a-prescriber' };
  return { disposition: 'order' };
}

/** True when this mention becomes an order proposal card. The one place the split is decided. */
export function producesOrder(med: AmbientMedication): boolean {
  return dispositionOf(med).disposition === 'order';
}

/**
 * Every medication mention that is NOT an order proposal, in payload order.
 *
 * The complement of what `to-proposals` emits for `medications[]`, by construction: both call
 * `producesOrder`, so a row can appear in one list or the other and never in both or neither.
 * `medication-renders-exactly-once.test.tsx` pins that as a property over arbitrary payloads
 * rather than as a spot check.
 */
export function toMedicationMentions(soap: SoapPayload | undefined): MedicationMention[] {
  const out: MedicationMention[] = [];
  soap?.medications?.forEach((med, i) => {
    const { disposition, refusal } = dispositionOf(med);
    if (disposition === 'order') return;
    out.push({
      id: `medication-${i}`,
      display: med.name,
      ...(med.sigVerbatim?.trim() ? { sig: med.sigVerbatim.trim() } : {}),
      disposition,
      ...(refusal ? { refusal } : {}),
    });
  });
  return out;
}

// The clinician-facing copy for each disposition and refusal is Signal DS's (`AMBIENT_MENTION_LABELS`,
// `AMBIENT_REFUSAL_NOTES`), rendered by the DS `AmbientScribeBlock`'s mention list. It was a PA copy
// here until PROD-2115; the strings were character-identical when it moved.
